/**
 * Configures per-route overrides of the home background, used by
 * `src/routes/HomeBackground.svelte` (new layout only).
 *
 * Any route whose path starts with `routePrefix` gets its background replaced by `color`
 * (behind the page content, transitioning smoothly - see `.home-background-route-override`
 * in HomeBackground.svelte) instead of the home's own photo/color/gradient background.
 *
 * Add more entries here to cover additional routes - no other code changes needed.
 */
import type { BackgroundColorToken } from './background_color_helpers';

export interface RouteBackgroundOverride {
	routePrefix: string;
	color: string;
}

export const ROUTE_BACKGROUND_OVERRIDES: RouteBackgroundOverride[] = [
	{ routePrefix: '/settings', color: '#000000' },
];

/** Returns the override color for the given `$location`, or `undefined` if none applies. */
export function getRouteBackgroundOverrideColor(location: string): string | undefined {
	return ROUTE_BACKGROUND_OVERRIDES.find((override) => location?.startsWith(override.routePrefix))
		?.color;
}

/**
 * Routes that adopt one of the standard background tokens instead of the home's own
 * background. Unlike `ROUTE_BACKGROUND_OVERRIDES` above, which paints an opaque color
 * over everything, these resolve through the normal image + overlay path, so the result
 * is identical to a dashboard configured with the same token (and cross-fades the same).
 */
export interface RouteBackgroundTokenOverride {
	routePrefix: string;
	token: BackgroundColorToken;
}

export const ROUTE_BACKGROUND_TOKEN_OVERRIDES: RouteBackgroundTokenOverride[] = [
	// Activity Log uses the Energy dashboard's default background. Pinned to the token
	// rather than read from the Energy dashboard's settings, which are user-editable —
	// re-theming Energy should not drag the Activity Log along with it.
	{ routePrefix: '/event_log', token: 'color-0' },
];

/** Returns the background token for the given `$location`, or `undefined` if none applies. */
export function getRouteBackgroundOverrideToken(location: string): BackgroundColorToken | undefined {
	return ROUTE_BACKGROUND_TOKEN_OVERRIDES.find((override) =>
		location?.startsWith(override.routePrefix),
	)?.token;
}
